import { coversTime, normalizeWeekHours, tenantClock } from "@/lib/scheduling/normalize-hours"
import { timeBoundHHMM } from "@/lib/schedule"
import type { Availability } from "@/types"

function withinValidity(availability: Availability, when: Date): boolean {
  const start = availability.startDate ? new Date(availability.startDate) : null
  const end = availability.endDate ? new Date(availability.endDate) : null
  if (start && !Number.isNaN(start.getTime()) && when.getTime() < start.getTime()) return false
  if (end && !Number.isNaN(end.getTime()) && when.getTime() > end.getTime()) return false
  return true
}

/**
 * Whether a set of availability windows holds at a GIVEN instant, on the
 * TENANT's clock and at minute granularity, inclusive on both ends. No window
 * means "always available".
 *
 * The windows go through `normalizeWeekHours`, so an item availability
 * published as a UTC time-of-day (§9.1) is read as the local window the
 * merchant authored — this is what closes the BR-12 debt of evaluating the
 * menu on the customer's browser clock.
 *
 * Unlike {@link isAvailableNow} this also honours the `startDate`/`endDate`
 * validity of each availability — a scheduled slot can land on a day outside
 * the item's season, which "now" never could.
 */
export function isAvailableAt(
  availabilityIds: string[] | undefined,
  availabilities: Availability[],
  when: Date,
  timeZone?: string | null,
): boolean {
  return holdsAt(availabilityIds, availabilities, when, timeZone, true)
}

/** Shared evaluation; `honourValidity` is what separates "at" from "now". */
export function holdsAt(
  availabilityIds: string[] | undefined,
  availabilities: Availability[],
  when: Date,
  timeZone: string | null | undefined,
  honourValidity: boolean,
): boolean {
  if (!availabilityIds || availabilityIds.length === 0) return true

  const clock = tenantClock(when, timeZone)

  return availabilityIds.some((id) => {
    const availability = availabilities.find((a) => a.id === id)
    if (!availability) return false
    if (honourValidity && !withinValidity(availability, when)) return false

    return normalizeWeekHours(availability.hours, timeZone).some(
      (window) => window.day === clock.day && coversTime(window, clock.time),
    )
  })
}

/**
 * Latest "HH:mm" an availability set still holds on the tenant's day of
 * `when`. Null when the set has no window that day, or no window at all.
 */
export function availabilityEndsAt(
  availabilityIds: string[] | undefined,
  availabilities: Availability[],
  when: Date,
  timeZone?: string | null,
): string | null {
  if (!availabilityIds?.length) return null

  const clock = tenantClock(when, timeZone)
  let latest: string | null = null

  for (const id of availabilityIds) {
    const availability = availabilities.find((a) => a.id === id)
    if (!availability) continue
    if (!withinValidity(availability, when)) continue
    for (const window of normalizeWeekHours(availability.hours, timeZone)) {
      if (window.day !== clock.day) continue
      const end = timeBoundHHMM(window.endTime)
      if (!latest || end > latest) latest = end
    }
  }

  return latest
}
